import { useEffect, useMemo, useState, useCallback } from 'react'
import TopBar from './components/TopBar.jsx'
import LeftSidebar from './components/LeftSidebar.jsx'
import CenterWorkspace from './components/CenterWorkspace.jsx'
import RightSidebar from './components/RightSidebar.jsx'
import ProcessingOverlay from './components/ProcessingOverlay.jsx'
import Toast from './components/Toast.jsx'
import EmptyState from './components/EmptyState.jsx'
import PageViewerModal from './components/PageViewerModal.jsx'
import { useProjectStore } from './store/projectStore.js'
import { persistState, loadPersistedState } from './lib/persistence.js'

function applyTheme(theme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const useDark =
    theme === 'dark' ||
    (theme === 'system' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  if (useDark) root.classList.add('dark')
  else root.classList.remove('dark')
}

export default function App() {
  const documents = useProjectStore((s) => s.documents)
  const isProcessing = useProjectStore((s) => s.isProcessing)
  const processingMessage = useProjectStore((s) => s.processingMessage)
  const toast = useProjectStore((s) => s.toast)
  const dismissToast = useProjectStore((s) => s.dismissToast)
  const selectedPageId = useProjectStore((s) => s.selectedPageId)
  const viewerPageId = useProjectStore((s) => s.viewerPageId)
  const closeViewer = useProjectStore((s) => s.closeViewer)
  const theme = useProjectStore((s) => s.theme)
  const undo = useProjectStore((s) => s.undo)
  const redo = useProjectStore((s) => s.redo)
  const setToolMode = useProjectStore((s) => s.setToolMode)
  const removePage = useProjectStore((s) => s.removePage)
  const hydrateFrom = useProjectStore((s) => s.hydrateFrom)

  const [leftOpen, setLeftOpen] = useState(false)
  const [rightOpen, setRightOpen] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  const hasDocuments = documents.length > 0

  const openLeft = useCallback(() => {
    setLeftOpen(true)
    setRightOpen(false)
  }, [])
  const openRight = useCallback(() => {
    setRightOpen(true)
    setLeftOpen(false)
  }, [])
  const closeDrawers = useCallback(() => {
    setLeftOpen(false)
    setRightOpen(false)
  }, [])

  // --- Theme ---------------------------------------------------------------
  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  // --- Hydrate from IndexedDB on first mount -------------------------------
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const saved = await loadPersistedState()
      if (!cancelled && saved) hydrateFrom(saved)
      setHydrated(true)
    })()
    return () => {
      cancelled = true
    }
  }, [hydrateFrom])

  // --- Auto-persist on changes --------------------------------------------
  useEffect(() => {
    if (!hydrated) return
    const unsub = useProjectStore.subscribe(
      (s) => [s.documents, s.pageOrder, s.exportOptions, s.signatures, s.formTemplates, s.theme, s.splitGroups],
      () => {
        const state = useProjectStore.getState()
        // throttle: skip if processing — avoid heavy churn during exports
        if (state.isProcessing) return
        persistState(state)
      },
      { equalityFn: (a, b) => a.every((v, i) => v === b[i]) }
    )
    return () => unsub()
  }, [hydrated])

  // --- Mobile: auto-open inspector on page selection ----------------------
  useEffect(() => {
    if (!selectedPageId) return
    if (typeof window === 'undefined') return
    if (window.matchMedia('(max-width: 767px)').matches) setRightOpen(true)
  }, [selectedPageId])

  // --- Keyboard shortcuts -------------------------------------------------
  useEffect(() => {
    const handler = (event) => {
      const tag = event.target?.tagName
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable
      const meta = event.metaKey || event.ctrlKey

      if (event.key === 'Escape') {
        setToolMode('select')
        closeDrawers()
        if (useProjectStore.getState().viewerPageId) closeViewer()
        return
      }

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
        return
      }
      if (meta && event.key.toLowerCase() === 'y') {
        event.preventDefault()
        redo()
        return
      }

      if (inField) return

      // Tool shortcuts
      const k = event.key.toLowerCase()
      const map = {
        v: 'select',
        s: 'stamp',
        r: 'redact',
        c: 'crop',
        h: 'highlight',
        i: 'ink',
        n: 'sticky',
        g: 'signature',
      }
      if (map[k]) {
        event.preventDefault()
        setToolMode(map[k])
        return
      }

      // Delete page
      if ((event.key === 'Delete' || event.key === 'Backspace') && useProjectStore.getState().selectedPageId) {
        event.preventDefault()
        removePage(useProjectStore.getState().selectedPageId)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [closeDrawers, redo, undo, setToolMode, removePage, closeViewer])

  const beforeUnloadGuard = useMemo(() => hasDocuments, [hasDocuments])
  useEffect(() => {
    if (!beforeUnloadGuard) return
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [beforeUnloadGuard])

  return (
    <>
      <div className="flex h-screen flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <TopBar
          onMenuClick={() => (leftOpen ? setLeftOpen(false) : openLeft())}
          onInspectorClick={() => (rightOpen ? setRightOpen(false) : openRight())}
          leftOpen={leftOpen}
          rightOpen={rightOpen}
        />
        <div className="relative flex flex-1 min-h-0">
          <LeftSidebar open={leftOpen} onClose={closeDrawers} />
          <main className="flex-1 min-w-0 overflow-hidden">
            {hasDocuments ? <CenterWorkspace /> : <EmptyState />}
          </main>
          <RightSidebar open={rightOpen} onClose={closeDrawers} />

          {(leftOpen || rightOpen) && (
            <button
              type="button"
              aria-label="Close menu"
              onClick={closeDrawers}
              className="fixed inset-0 top-[57px] z-30 bg-slate-900/35 backdrop-blur-[1px] md:hidden"
            />
          )}
        </div>
        {isProcessing && <ProcessingOverlay message={processingMessage} />}
        {toast && <Toast toast={toast} onDismiss={dismissToast} />}
        {viewerPageId && (
          <PageViewerModal pageId={viewerPageId} onClose={closeViewer} />
        )}
      </div>
    </>
  )
}
